import { SvgIconComponent } from '@mui/icons-material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import {
	Box,
	Button,
	IconButton,
	InputBase,
	Tooltip,
	Typography,
} from '@mui/material'
import { useMemo } from 'react'

import { LANGUAGE_KEY } from '@/context/Locale/Locale'
import { useAppContext } from '@/hooks'
import { CATALOG_UI, catalogSidebarSx } from '@/styles/catalogUi'

import { BranchFilterSession } from '../branchFilterUi'
import { branchTreeVisibility } from '../branchTree'
import { BranchRecord, BranchStatus } from '../types'
import { BranchTreeItem, BranchTreeNode } from './BranchTreeItem'

const STATUS_FILTERS: {
	icon: SvgIconComponent
	labelKey: LANGUAGE_KEY
	status: BranchStatus
}[] = [
	{
		icon: CheckCircleOutlineOutlinedIcon,
		labelKey: 'BRANCHES_OPENED',
		status: 'ACTIVE',
	},
	{
		icon: LockOutlinedIcon,
		labelKey: 'BRANCHES_STATUS_LOCKED',
		status: 'LOCKED',
	},
	{
		icon: InventoryOutlinedIcon,
		labelKey: 'BRANCHES_STATUS_ARCHIVED',
		status: 'ARCHIVED',
	},
	{
		icon: DeleteOutlineOutlinedIcon,
		labelKey: 'BRANCHES_STATUS_DELETED',
		status: 'DELETED',
	},
]

const filterButtonSx = (active: boolean) => ({
	width: 26,
	height: 26,
	borderRadius: '7px',
	border: `1px solid ${active ? '#1F4954' : CATALOG_UI.colors.chipBorder}`,
	bgcolor: active ? '#1F4954' : '#fff',
	color: active ? '#fff' : '#6B7B80',
	'&:hover': { bgcolor: active ? '#1F4954' : 'rgba(31,73,84,0.06)' },
})

type BranchesSidebarProps = {
	activeBranchId: string | null
	branchId: string | null
	branches: BranchRecord[]
	canCreate: boolean
	expandedIds: string[]
	filter: BranchFilterSession
	onOpenCreate: () => void
	onOpenMenu: (anchor: HTMLElement, branch: BranchRecord) => void
	onSelectBranch: (branchId: string) => void
	onToggleExpanded: (branchId: string) => void
	onToggleProtected: () => void
	onToggleStatus: (status: BranchStatus) => void
	search: string
	setSearch: (value: string) => void
}

export const BranchesSidebar = ({
	activeBranchId,
	branchId,
	branches,
	canCreate,
	expandedIds,
	filter,
	onOpenCreate,
	onOpenMenu,
	onSelectBranch,
	onToggleExpanded,
	onToggleProtected,
	onToggleStatus,
	search,
	setSearch,
}: BranchesSidebarProps) => {
	const { t } = useAppContext()

	const { dimmedIds, visibleIds } = useMemo(
		() => branchTreeVisibility(branches, filter, activeBranchId),
		[activeBranchId, branches, filter],
	)

	const filtered = useMemo(() => {
		const visible = branches.filter((branch) => visibleIds.has(branch.id))
		const query = search.trim().toLowerCase()
		if (!query) return visible
		return visible.filter((branch) => branch.name.toLowerCase().includes(query))
	}, [branches, search, visibleIds])

	const expanded = useMemo(() => new Set(expandedIds), [expandedIds])

	const byId = useMemo(() => {
		const map = new Map<string, BranchTreeNode>()
		filtered.forEach((branch) => map.set(branch.id, { ...branch, children: [] }))
		filtered.forEach((branch) => {
			if (!branch.sourceBranchId) return
			const parent = map.get(branch.sourceBranchId)
			const current = map.get(branch.id)
			if (parent && current) parent.children.push(current)
		})
		return map
	}, [filtered])

	const roots = useMemo(
		() =>
			Array.from(byId.values())
				.filter((branch) => !branch.sourceBranchId || !byId.has(branch.sourceBranchId))
				.sort((a, b) => a.name.localeCompare(b.name)),
		[byId],
	)

	return (
		<Box component="aside" sx={catalogSidebarSx} data-testid="branches-sidebar">
			<Box sx={{ p: 2, pb: 1.5 }}>
				<Button
					fullWidth
					variant="contained"
					disableElevation
					startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
					disabled={!canCreate}
					onClick={onOpenCreate}
					data-testid="branches-button-create"
					sx={{ borderRadius: '9px', height: 34, fontSize: 12.5, mb: 1.5 }}
				>
					{t('BRANCHES_CREATE')}
				</Button>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						height: 36,
						px: 1.25,
						border: `1px solid ${CATALOG_UI.colors.chipBorder}`,
						borderRadius: '9px',
						bgcolor: '#fff',
					}}
				>
					<SearchOutlinedIcon sx={{ color: '#9ca3af', fontSize: 15 }} />
					<InputBase
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder={t('BRANCHES_SEARCH_PLACEHOLDER')}
						fullWidth
						inputProps={{ 'data-testid': 'text-input-branch-search' }}
						sx={{ fontSize: 13 }}
					/>
				</Box>
				<Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
					{STATUS_FILTERS.map(({ icon: Icon, labelKey, status }) => {
						const active = filter.statuses.includes(status)
						return (
							<Tooltip key={status} title={t(labelKey)} arrow>
								<IconButton
									aria-pressed={active}
									onClick={() => onToggleStatus(status)}
									data-testid={`branches-filter-${status.toLowerCase()}`}
									sx={filterButtonSx(active)}
								>
									<Icon sx={{ fontSize: 14 }} />
								</IconButton>
							</Tooltip>
						)
					})}
					<Tooltip title={t('BRANCHES_PROTECTED')} arrow>
						<IconButton
							aria-pressed={filter.protectedOnly}
							onClick={onToggleProtected}
							data-testid="branches-filter-protected"
							sx={filterButtonSx(filter.protectedOnly)}
						>
							<ShieldOutlinedIcon sx={{ fontSize: 14 }} />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1, pb: 2 }}>
				<Typography
					sx={{
						mb: 0.75,
						px: 1,
						fontSize: 10,
						fontWeight: 700,
						letterSpacing: '0.06em',
						textTransform: 'uppercase',
						color: CATALOG_UI.colors.subtle,
					}}
				>
					{t('BRANCHES_TREE')}
				</Typography>
				{roots.map((branch) => (
					<BranchTreeItem
						key={branch.id}
						activeBranchId={activeBranchId}
						branch={branch}
						branchId={branchId}
						dimmedIds={dimmedIds}
						expandedIds={expanded}
						onOpenMenu={onOpenMenu}
						onSelectBranch={onSelectBranch}
						onToggleExpanded={onToggleExpanded}
					/>
				))}
				{roots.length === 0 && (
					<Typography sx={{ px: 1.5, py: 2, fontSize: 12, color: '#9ca3af' }}>
						{t('SEARCH_EMPTY')}
					</Typography>
				)}
			</Box>
		</Box>
	)
}
